import {
  Brick,
  registerBrick,
  Query,
  Predicate,
  Transaction,
} from 'olympe';
import BCP from '../../../common/section_bcp/DM_BCP';
import { getTerminatedRequests } from '../../section_risk/RiskCrisis';
import { isNumeric, validateEmail, getUserName, zeroPad } from './utils';
import { executeLambda } from '@olympeio/core';

export default class ValidateMassImportExcel extends Brick {
  /**
   * @override
   * @protected
   * @param {!BrickContext} $
   * @param {?Array<Object>} excelData
   * @param {!string} graphToken
   * @param {*} fetchBcpPlantMaterialSpec
   * @param {function()} forwardEvent
   * @param {?Array<Object>} setOutput
   * @param {?Array<Object>} setError
   * @param {function(Map)} setOriginalExcelData
   */

  async update(
    $,
    [excelData, graphToken,fetchBcpPlantMaterialSpec],
    [forwardEvent, setOutput, setError, setOriginalExcelData]
  ) {
    const plant_id_length = 4;
    const material_id_length = 18;
    const specification_length = 12;
    const errorLogs = [];
    const finalSet = new Set();
    const origExcelData = new Map();

    // Function to fetch plant materials and specification details from the BCP system
   async function fetchPlantMaterialsDetails(plantID, materialID, specification) {
    try {
        // Execute the lambda function to fetch the data
        const bodyResult = await executeLambda($, fetchBcpPlantMaterialSpec, [plantID, materialID, specification]);
        return bodyResult;
    } catch (error) {
        // Handle errors during the data fetch process
        throw new Error(`Error in fetching BCP data of PlantID:${plantID}, Material ID:${materialID} & specfication ${specification} : ${error.message}`);
    }
  }

    if (excelData && Array.isArray(excelData) && excelData.length > 0) {
      for (const item of excelData) {
        const errors = [];
        const newItem = JSON.parse(JSON.stringify(item));

        const plantId = String(item['Plant ID']);
        const materialId = item['Material ID'];
        const specification = item['Specification'];
        const transformedMaterialId = zeroPad(materialId);

        //validate plantID 4 digit/alphanumeric string
        if (
          !item.hasOwnProperty('Plant ID') ||
          plantId.length !== plant_id_length ||
          !plantId.match(/^[A-Za-z0-9_.]+$/)
        ) {
          errors.push('Invalid PlantID');
        }
        //validate MaterialID <=18 digit/alphanumeric string
        if (
          !item.hasOwnProperty('Material ID') ||
          String(materialId).length > material_id_length ||
          !isNumeric(materialId)
        ) {
          errors.push('Invalid Material ID');
        }
        //validate Specification = 12 digit string or null/undefined
        if (
          item.hasOwnProperty('Specification') &&
          (String(specification).length !== specification_length ||
          !isNumeric(specification))
        ) {
          errors.push('Invalid Specification');
        }
        //validate email ID is valid
        if (
          !validateEmail(item['PS/BP email']) ||
          !validateEmail(item['Requestor Email']) ||
          !validateEmail(item['Buyer Email'])
        ) {
          errors.push('Invalid PS/BP email or Buyer Email or Requestor Email');
        }

        // If no validation errors, proceed to fetch the plant materials & speciication details
       if(errors.length === 0){
        const spec = typeof specification =='undefined'? null :specification; // Handle undefined specification
        const fetchedData = await fetchPlantMaterialsDetails(plantId, transformedMaterialId, spec); // Fetch matching data from the database
        const dbMatch = JSON.parse(fetchedData[0]).results; 
        if (dbMatch.length === 0){
          errors.push('Plant ID, Material ID, and Specification combination not matched in DB'); // Error if no match found in DB
        }
      }

        //if record is valid then execute DB call followed by data transformation
        if (errors.length === 0) {
          const queryResult = await Query.instancesOf(BCP)
            .filter(
              Predicate.and(
                Predicate.equals(BCP.materialIdProp, transformedMaterialId),
                Predicate.equals(BCP.plantIdProp, plantId)
              )
            )
            .execute();

          // if plantID & materialID combination does not exist, queryResult.toArray().length is 0
          const isUnique = queryResult.toArray().length === 0;

          // check workflowState of existing BCP request(based on plantID and materialID combination), If state is in (Completed, Rejected), then delete the existing request and proceed with creating new request else push in errorLogs array.
          if (!isUnique) {
            try {
              const finishedBCP = (
                await getTerminatedRequests(
                  [{ materialId: transformedMaterialId, plantId }],
                  $
                )
              )?.[0];
              if (
                finishedBCP?.get(BCP.materialIdProp) ===
                  transformedMaterialId &&
                finishedBCP?.get(BCP.plantIdProp) === plantId
              ) {
                const t = new Transaction();
                t.delete(finishedBCP);
                await t.execute();
              } else {
                errors.push(
                  `There is an existing ongoing BCP request found for plantID ${plantId} and materialID ${transformedMaterialId}!`
                );
              }
            } catch (e) {
              errors.push(
                `Failed to get terminated request or delete existing requests: ${e.message}`
              );
            }
          }

          /*
              transformation of record fields to desired format
              requestor information, EITHER
              requestor given/first name and family/last/surname;requestor email
              BP (business partner/buyer) info: display name;email
              PM (procurement manager) info: display name;email
              materialId pad leading zeros untill 18 chars
            */

          newItem['Material ID'] = transformedMaterialId;

          try {
            const bpFullName = await getUserName(
              item['PS/BP email'],
              graphToken
            );
            const pmFullName = await getUserName(
              item['Buyer Email'],
              graphToken
            );
            const requestorFullName = await getUserName(
              item['Requestor Email'],
              graphToken,
              false
            );
            if (
              typeof bpFullName === 'string' &&
              typeof pmFullName === 'string' &&
              typeof requestorFullName === 'string'
            ) {
              newItem['PS/BP email'] = bpFullName;
              newItem['Buyer Email'] = pmFullName;
              newItem['Requestor Email'] = requestorFullName;
            } else {
              errors.push('Failed to fetch PS/BP or Buyer or Requestor details');
            }
          } catch (e) {
            errors.push('Failed to fetch PS/BP or Buyer or Requestor details');
          }
        }

        if (errors.length > 0) {
          errorLogs.push({
            ...item,
            Step: 'Validation of BCP input data',
            Reason: errors.toString(),
          });
        } else {
          // insert to Set, it will ignore duplicate records
          finalSet.add(newItem);
        }
        // Add plantID + transformed_materialId as key and item as value to the origExcelData Map in order to generate errorLogs in Creation phase
        origExcelData.set(plantId + transformedMaterialId, item);
      }
    }
    setOutput(Array.from(finalSet));
    setError(Array.from(errorLogs));
    setOriginalExcelData(origExcelData);
    forwardEvent();
  }
}

registerBrick('0190e5cc159d83ecb4c1', ValidateMassImportExcel);
